import axios from 'axios';

/**
 * Where the transport's HTTP status is kept, on both the resolved body and the
 * error this interceptor throws. The payment analytics report it (see
 * `getHttpStatus` in core/payment/utils/payment-error-state.ts) and it is the
 * only thing a bare 502 from our own server carries.
 *
 * Non-enumerable on purpose: the resolved body is spread into other payloads
 * and serialized downstream, and a visible extra key would travel with them.
 *
 * Not to be confused with a `status` field inside the body — that one is the
 * backend's own ('failed', 'requires_action', a Stripe intent status).
 */
const HTTP_STATUS_KEY = '__http_status';

const attachHttpStatus = (target, status) => {
  if (!target || typeof target !== 'object' || !status) {
    return target;
  }

  try {
    Object.defineProperty(target, HTTP_STATUS_KEY, {
      value: status,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch (ex) {
    // Frozen body — the status just stays unavailable downstream.
  }

  return target;
};

export const request = (baseURL, headers = {}) => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  instance.interceptors.response.use(
    (response) => attachHttpStatus(response.data, response.status),
    async (error) => {
      const r = error?.response;
      const d = r?.data?.data || r?.data;
      if (d?.message || d?.three_d_secure_action_token_id) {
        const cause = Error(JSON.stringify(d, null, 2));
        cause.name = 'Server Error';
        const e = Error(d.message, { cause });
        e.name = d.name;
        e.status = d.status;
        e.code = d.code;
        e.data = d;
        // `e.status` above is the backend's own field, not the transport's —
        // the real HTTP status would otherwise be dropped with `error.response`.
        attachHttpStatus(e, r?.status);
        attachHttpStatus(d, r?.status);
        throw e;
      }
      throw error;
    }
  )
  return instance;
};
